<template>
  <div class="cuentas-cobrar-container">
    <Toast></Toast>
    <ConfirmDialog />

    <!-- Header -->
    <div class="header-section">
      <h2>
        <i class="pi pi-calendar-plus mr-2"></i>CUENTAS POR COBRAR - MENSUAL
      </h2>
    </div>

    <!-- Verificación de Caja -->
    <div v-if="!cargandoComponente" class="loading-section">
      <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" />
      <p>Verificando caja...</p>
    </div>

    <div v-else-if="!permitirPago" class="alert-section">
      <Message :severity="'warn'" :closable="false">
        <div class="flex align-items-center justify-content-between">
          <span
            ><i class="pi pi-exclamation-triangle mr-2"></i>DEBE ABRIR LA
            CAJA</span
          >
          <Button
            label="APERTURAR CAJA"
            class="p-button-success"
            icon="pi pi-lock-open"
            @click="$router.push({ name: 'caja' })"
          />
        </div>
      </Message>
    </div>

    <!-- Contenido principal -->
    <div v-else>
      <!-- Filtros -->
      <div class="card p-3 mb-3">
        <div class="grid">
          <div class="col-12 md:col-3">
            <label class="mb-2 block font-bold"
              ><i class="pi pi-calendar mr-1"></i>MES</label
            >
            <Dropdown
              v-model="mesSeleccionado"
              :options="meses"
              optionLabel="nombre"
              optionValue="valor"
              placeholder="Mes"
              class="w-full"
            />
          </div>

          <div class="col-12 md:col-2">
            <label class="mb-2 block font-bold"
              ><i class="pi pi-calendar mr-1"></i>AÑO</label
            >
            <Dropdown
              v-model="anioSeleccionado"
              :options="anios"
              placeholder="Año"
              class="w-full"
            />
          </div>

          <div class="col-12 md:col-3 flex align-items-end">
            <Button
              label="CARGAR CLIENTES"
              icon="pi pi-search"
              class="p-button-primary w-full"
              @click="cargarClientes"
              :loading="cargandoClientes"
            />
          </div>

          <div class="col-12 md:col-4 flex align-items-end">
            <small class="text-500">
              Productos: {{ productosLicencia.length }} | Filtrados:
              {{ productosFiltrados.length }} | Médicos:
              {{ comisionistas.length }}
            </small>
          </div>
        </div>
      </div>

      <!-- Resumen -->
      <div v-if="clientesCobro.length > 0" class="grid mb-3">
        <div class="col-6 md:col-3">
          <div class="stat-card stat-total">
            <i class="pi pi-users"></i>
            <div>
              <small>TOTAL</small>
              <h3>{{ clientesCobro.length }}</h3>
            </div>
          </div>
        </div>

        <div class="col-6 md:col-3">
          <div class="stat-card stat-pending">
            <i class="pi pi-clock"></i>
            <div>
              <small>PENDIENTES</small>
              <h3>{{ clientesPendientes }}</h3>
            </div>
          </div>
        </div>

        <div class="col-6 md:col-3">
          <div class="stat-card stat-amount">
            <i class="pi pi-dollar"></i>
            <div>
              <small>A COBRAR</small>
              <h3>{{ formatCurrency(totalCobrar) }}</h3>
            </div>
          </div>
        </div>

        <div class="col-6 md:col-3">
          <div class="stat-card stat-commission">
            <i class="pi pi-percentage"></i>
            <div>
              <small>COMISIONES</small>
              <h3>{{ formatCurrency(totalComisiones) }}</h3>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div v-if="clientesCobro.length > 0" class="card">
        <DataTable
          :value="clientesCobro"
          :paginator="true"
          :rows="15"
          responsiveLayout="scroll"
          dataKey="id"
          :loading="cargandoClientes"
          class="p-datatable-sm"
          showGridlines
          stripedRows
        >
          <Column field="nombre" header="CLIENTE" style="min-width: 150px">
            <template #body="{ data }">
              <strong>{{ data.nombre }}</strong>
            </template>
          </Column>

          <Column header="PRODUCTO" style="min-width: 200px">
            <template #body="{ data }">
              <Dropdown
                v-model="data.producto_id"
                :options="productosFiltrados"
                optionLabel="descripcion"
                optionValue="id"
                placeholder="Seleccione"
                @change="actualizarPrecio(data)"
                :filter="true"
                class="w-full p-inputtext-sm compact-dropdown"
              />
            </template>
          </Column>

          <Column header="PRECIO" style="width: 100px">
            <template #body="{ data }">
              <InputNumber
                v-model="data.precio_venta"
                :minFractionDigits="2"
                mode="decimal"
                class="w-full p-inputtext-sm"
              />
            </template>
          </Column>

          <Column header="TRABAJADOR" style="min-width: 180px">
            <template #body="{ data }">
              <Dropdown
                v-model="data.comisionista_id"
                :options="comisionistas"
                optionLabel="nombre_completo"
                optionValue="id"
                placeholder="Sin comisión"
                @change="onComisionistaChange(data)"
                :filter="true"
                :showClear="true"
                class="w-full p-inputtext-sm compact-dropdown"
                :disabled="!data.producto_id"
              />
            </template>
          </Column>

          <Column header="COMISIÓN" style="width: 100px">
            <template #body="{ data }">
              <InputNumber
                v-model="data.monto_comision"
                :minFractionDigits="2"
                mode="decimal"
                class="w-full p-inputtext-sm"
                :disabled="!data.comisionista_id"
                placeholder="0.00"
              />
            </template>
          </Column>

          <Column header="ESTADO" style="width: 110px">
            <template #body="{ data }">
              <Tag
                :value="data.estado_cobro_texto"
                :severity="getSeverity(data.estado_cobro)"
              />
            </template>
          </Column>

          <Column style="width: 60px">
            <template #header>
              <i class="pi pi-times"></i>
            </template>
            <template #body="{ data, index }">
              <Button
                icon="pi pi-times"
                class="p-button-danger p-button-sm p-button-rounded p-button-text"
                v-tooltip.top="'Quitar'"
                @click="quitarCliente(index)"
                :disabled="data.estado_cobro === 'registrado'"
              />
            </template>
          </Column>
        </DataTable>

        <!-- Botón Masivo -->
        <div class="flex justify-content-end mt-3 pr-3 pb-3">
          <Button
            label="REGISTRAR TODAS LAS VENTAS A CRÉDITO"
            icon="pi pi-save"
            class="p-button-lg p-button-success"
            @click="registrarVentasMasivas"
            :loading="registrandoMasivo"
            :disabled="clientesPendientes === 0"
          />
        </div>
      </div>

      <!-- Sin datos -->
      <div v-else-if="!cargandoClientes" class="card p-5 text-center">
        <i class="pi pi-inbox text-6xl text-400 mb-3"></i>
        <h3 class="text-500">NO HAY CLIENTES CON COBRO MENSUAL</h3>
      </div>
    </div>
  </div>
</template>

<script>
import CajaService from "@/service/CajaService";
import ClienteService from "@/service/ClienteService";
import ProductService from "@/service/ProductService";
import MedicoService from "@/service/MedicoService";
import VentaService from "@/service/VentaService";

export default {
  name: "CuentasPorCobrar",

  data() {
    return {
      cargandoComponente: false,
      permitirPago: false,
      cajaActiva: null,
      cargandoClientes: false,
      registrandoMasivo: false,

      mesSeleccionado: new Date().getMonth() + 1,
      anioSeleccionado: new Date().getFullYear(),
      clientesCobro: [],
      productosLicencia: [],
      comisionistas: [],

      meses: [
        { valor: 1, nombre: "ENERO" },
        { valor: 2, nombre: "FEBRERO" },
        { valor: 3, nombre: "MARZO" },
        { valor: 4, nombre: "ABRIL" },
        { valor: 5, nombre: "MAYO" },
        { valor: 6, nombre: "JUNIO" },
        { valor: 7, nombre: "JULIO" },
        { valor: 8, nombre: "AGOSTO" },
        { valor: 9, nombre: "SEPTIEMBRE" },
        { valor: 10, nombre: "OCTUBRE" },
        { valor: 11, nombre: "NOVIEMBRE" },
        { valor: 12, nombre: "DICIEMBRE" },
      ],
    };
  },

  cajaService: null,
  clienteService: null,
  productService: null,
  medicoService: null,
  ventaService: null,

  created() {
    this.cajaService = new CajaService();
    this.clienteService = new ClienteService();
    this.productService = new ProductService();
    this.medicoService = new MedicoService();
    this.ventaService = new VentaService();
  },

  mounted() {
    this.verificarCajaAbierta();
    this.cargarDatosIniciales();
  },

  computed: {
    anios() {
      const anioActual = new Date().getFullYear();
      return [anioActual - 1, anioActual, anioActual + 1];
    },

    productosFiltrados() {
      if (!this.productosLicencia.length) return [];

      const nombreMes = this.getNombreMes(this.mesSeleccionado);
      const filtrados = this.productosLicencia.filter((p) =>
        p.descripcion.toUpperCase().includes(nombreMes)
      );

      return filtrados.length > 0 ? filtrados : this.productosLicencia;
    },

    totalCobrar() {
      return this.clientesCobro
        .filter((c) => c.estado_cobro === "pendiente")
        .reduce((sum, c) => sum + parseFloat(c.precio_venta || 0), 0);
    },

    totalComisiones() {
      return this.clientesCobro
        .filter((c) => c.estado_cobro === "pendiente")
        .reduce((sum, c) => sum + parseFloat(c.monto_comision || 0), 0);
    },

    clientesPendientes() {
      return this.clientesCobro.filter((c) => c.estado_cobro === "pendiente")
        .length;
    },
  },

  methods: {
    formatCurrency(value) {
      return parseFloat(value || 0).toFixed(2);
    },

    verificarCajaAbierta() {
      this.cajaService.verificarCajaAbierta().then((response) => {
        if (response.caja.length > 0) {
          this.cajaActiva = response.caja[0];
          this.permitirPago = true;
        } else {
          this.permitirPago = false;
        }
        this.cargandoComponente = true;
      });
    },

    async cargarDatosIniciales() {
      try {
        const respProductos = await this.productService.getProductosLicencia();

        if (respProductos.success) {
          this.productosLicencia = respProductos.productos || [];
        }

        const respMedicos = await this.medicoService.getMedicosAll();
        this.comisionistas = respMedicos.medicos || [];
      } catch (error) {
        console.error("Error completo:", error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail: "Error al cargar datos: " + error.message,
          life: 5000,
        });
      }
    },

    async cargarClientes() {
      if (!this.permitirPago) {
        this.$toast.add({
          severity: "warn",
          summary: "Advertencia",
          detail: "Debe abrir una caja primero",
          life: 3000,
        });
        return;
      }

      this.cargandoClientes = true;

      try {
        const resp = await this.clienteService.getClientesCobro({
          mes: this.mesSeleccionado,
          anio: this.anioSeleccionado,
        });

        if (resp.success) {
          this.clientesCobro = resp.clientes.map((cliente) => ({
            ...cliente,
            producto_id: null,
            precio_venta: parseFloat(cliente.precio_mensual || 100),
            comisionista_id: null,
            monto_comision: 0,
            estado_cobro: cliente.estado_cobro || "pendiente",
            estado_cobro_texto: this.getEstadoTexto(cliente.estado_cobro),
          }));

          this.autoSeleccionarProducto();

          this.$toast.add({
            severity: "success",
            summary: "Clientes Cargados",
            detail: `${resp.total_clientes} clientes encontrados`,
            life: 3000,
          });
        }
      } catch (error) {
        console.error("Error al cargar clientes:", error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail: "Error al cargar clientes",
          life: 3000,
        });
      } finally {
        this.cargandoClientes = false;
      }
    },

    autoSeleccionarProducto() {
      if (this.productosFiltrados.length === 1) {
        const productoUnico = this.productosFiltrados[0];

        this.clientesCobro.forEach((cliente) => {
          if (cliente.estado_cobro === "pendiente") {
            cliente.producto_id = productoUnico.id;
            this.actualizarPrecio(cliente);
          }
        });
      } else if (this.productosFiltrados.length === 0) {
        this.$toast.add({
          severity: "warn",
          summary: "Sin Productos",
          detail: `No hay productos de licencia para ${this.getNombreMes(
            this.mesSeleccionado
          )}`,
          life: 5000,
        });
      }
    },

    actualizarPrecio(cliente) {
      const producto = this.productosLicencia.find(
        (p) => p.id === cliente.producto_id
      );

      if (producto) {
        if (!cliente.precio_mensual || cliente.precio_mensual === 0) {
          cliente.precio_venta = parseFloat(
            producto.precio_venta || producto.precio_sugerido || 100
          );
        }
      }
    },

    onComisionistaChange(cliente) {
      if (!cliente.comisionista_id) {
        cliente.monto_comision = 0;
        return;
      }

      const producto = this.productosLicencia.find(
        (p) => p.id === cliente.producto_id
      );

      if (producto && producto.comision_medico) {
        cliente.monto_comision = parseFloat(producto.comision_medico);
      } else {
        cliente.monto_comision = 100;
      }
    },

    quitarCliente(index) {
      this.$confirm.require({
        header: "Confirmación",
        message: "¿Desea quitar este cliente de la lista?",
        icon: "pi pi-exclamation-triangle",
        accept: () => {
          this.clientesCobro.splice(index, 1);
          this.$toast.add({
            severity: "info",
            summary: "Cliente Removido",
            detail: "Cliente quitado",
            life: 3000,
          });
        },
      });
    },

    puedeRegistrar(cliente) {
      return (
        cliente.producto_id &&
        cliente.precio_venta > 0 &&
        cliente.estado_cobro === "pendiente" &&
        this.cajaActiva
      );
    },

    async registrarVenta(cliente) {
      if (!this.puedeRegistrar(cliente)) {
        this.$toast.add({
          severity: "warn",
          summary: "Advertencia",
          detail: "Complete los datos requeridos",
          life: 3000,
        });
        return;
      }

      try {
        const producto = this.productosLicencia.find(
          (p) => p.id === cliente.producto_id
        );

        if (!producto) {
          this.$toast.add({
            severity: "error",
            summary: "Error",
            detail: "No se encontró el producto seleccionado",
            life: 3000,
          });
          return;
        }

        const baseProducto = {
          producto_id: parseInt(cliente.producto_id),
          nombre_producto: producto.descripcion,
          cantidad_venta: 1,
          ultimo_precio: parseFloat(cliente.precio_venta),
          precio_compra: 0,
          descuento_neto: 0,
          descuento_porcentaje: 0,
          precio_venta: parseFloat(cliente.precio_venta),
          tipo_catalogo: 2,
          lote: null,
          fecha_vence: null,
          nombre_fabrica: "SERVICIOS",
          comision_medico: 0,
        };

        if (cliente.comisionista_id) {
          const nombreMedico = this.getNombreComisionista(
            cliente.comisionista_id
          );

          baseProducto.comision_medico = parseFloat(
            cliente.monto_comision || 0
          );
          baseProducto.nombre_medico = nombreMedico;
          baseProducto.medico_id = parseInt(cliente.comisionista_id);
        }

        const payload = {
          cliente_id: parseInt(cliente.id),
          razon_social: cliente.nombre,
          nit: "0",
          total_cancelar: parseFloat(cliente.precio_venta),
          descuento: 0,
          efectivo: 0,
          caja_id: parseInt(this.cajaActiva.id),
          sucursal_id: parseInt(this.cajaActiva.sucursal_id),
          metodo_pago_id: 1, // EFECTIVO por defecto
          tipo_pago_id: 2, // CREDITO
          observacion: `LICENCIA - ${this.getNombreMes(this.mesSeleccionado)} ${
            this.anioSeleccionado
          }`,
          facturado: 0,
          fact_indicador: 0,
          venta_credito: true,
          medico_id: cliente.comisionista_id
            ? parseInt(cliente.comisionista_id)
            : null,
          nombre_medico: cliente.comisionista_id
            ? this.getNombreComisionista(cliente.comisionista_id)
            : null,
          productos: [baseProducto],
          dias_credito: 30,
          monto_pago: 0, // Sin pago inicial
          a_cuenta: 0, // Sin anticipo
          interes_venta_credito: 0,
          monto_interes_venta_credito: 0,
        };

        const resp = await this.ventaService.crear(payload);

        if (resp.status === 200) {
          this.$toast.add({
            severity: "success",
            summary: "Éxito",
            detail: "Venta registrada correctamente",
            life: 3000,
          });
          cliente.estado_cobro = "registrado";
          cliente.estado_cobro_texto = "REGISTRADO";
          cliente.venta_id = resp.venta.id;
        } else {
          this.$toast.add({
            severity: "error",
            summary: "Error",
            detail: resp.mensaje || "Error al registrar venta",
            life: 5000,
          });
        }
      } catch (error) {
        console.error("Error completo:", error);
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail:
            "Error al registrar venta: " +
            (error.response?.data?.mensaje || error.message),
          life: 5000,
        });
      }
    },

    async registrarVentasMasivas() {
      const pendientes = this.clientesCobro.filter((c) =>
        this.puedeRegistrar(c)
      );

      if (pendientes.length === 0) {
        this.$toast.add({
          severity: "warn",
          summary: "Advertencia",
          detail: "No hay clientes pendientes",
          life: 3000,
        });
        return;
      }

      this.$confirm.require({
        header: "Confirmación",
        message: `¿Desea registrar ${pendientes.length} ventas a crédito?`,
        icon: "pi pi-exclamation-triangle",
        accept: async () => {
          this.registrandoMasivo = true;
          let exitosos = 0;
          let errores = 0;

          for (const cliente of pendientes) {
            try {
              await this.registrarVenta(cliente);
              exitosos++;
              await this.sleep(300);
            } catch (error) {
              errores++;
              console.error(error);
            }
          }

          this.registrandoMasivo = false;

          this.$toast.add({
            severity: "success",
            summary: "Proceso Completado",
            detail: `Exitosos: ${exitosos}, Errores: ${errores}`,
            life: 5000,
          });

          if (exitosos > 0) {
            this.clientesCobro = this.clientesCobro.filter(
              (c) => c.estado_cobro !== "registrado"
            );

            if (this.clientesCobro.length === 0) {
              this.$toast.add({
                severity: "info",
                summary: "Completado",
                detail: "Todas las ventas fueron registradas",
                life: 3000,
              });
            }
          }
        },
      });
    },

    getNombreMes(mes) {
      return this.meses.find((m) => m.valor === mes)?.nombre || "";
    },

    getNombreComisionista(id) {
      if (!id) return null;
      return (
        this.comisionistas.find((c) => c.id === id)?.nombre_completo || null
      );
    },

    getEstadoTexto(estado) {
      return estado === "registrado" ? "REGISTRADO" : "PENDIENTE";
    },

    getSeverity(estado) {
      return estado === "registrado" ? "success" : "warning";
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
  },

  watch: {
    mesSeleccionado() {
      if (this.clientesCobro.length > 0) {
        this.clientesCobro.forEach((cliente) => {
          if (cliente.estado_cobro === "pendiente") {
            cliente.producto_id = null;
            cliente.precio_venta = parseFloat(cliente.precio_mensual || 100);
            cliente.comisionista_id = null;
            cliente.monto_comision = 0;
          }
        });

        this.$nextTick(() => {
          this.autoSeleccionarProducto();
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.cuentas-cobrar-container {
  padding: 1rem;
  max-width: 1600px;
  margin: 0 auto;
}

.header-section h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  color: #2c3e50;
}

.loading-section {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  i {
    font-size: 2rem;
  }

  small {
    display: block;
    font-size: 0.75rem;
    color: #6c757d;
  }

  h3 {
    margin: 0;
    font-size: 1.5rem;
  }

  &.stat-total i {
    color: #3b82f6;
  }
  &.stat-pending i {
    color: #f59e0b;
  }
  &.stat-amount i {
    color: #10b981;
  }
  &.stat-commission i {
    color: #8b5cf6;
  }
}

::v-deep(.compact-dropdown) {
  .p-dropdown {
    font-size: 0.75rem;
  }

  .p-dropdown-label {
    padding: 0.3rem 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .p-dropdown-trigger {
    width: 2rem;
  }
}

::v-deep(.p-datatable) {
  font-size: 0.8rem;

  .p-datatable-thead > tr > th {
    padding: 0.4rem;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .p-datatable-tbody > tr > td {
    padding: 0.4rem;
  }
}

::v-deep(.p-inputtext-sm) {
  font-size: 0.75rem;
  padding: 0.3rem 0.5rem;
}
</style>
